import React from 'react';
import { motion } from 'framer-motion';
import { Bot, Brain, Code, ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';

const Services = () => {
  const whatsappLink = "https://wa.me/919353316112?text=Hi%20LitheForce!%20I'm%20interested%20in%20your%20AI%20solutions.";
  
  const services = [
    {
      icon: Bot,
      tier: 'Tier 1',
      title: 'AI Chatbots',
      price: '₹50,000 - ₹2,00,000',
      timeline: '2-4 weeks',
      perfectFor: [
        'Customer support automation',
        'Sales qualification',
        'Order taking (WhatsApp/Web)',
        'FAQ automation',
      ],
      features: [
        'WhatsApp/Website integration',
        'Natural language understanding',
        'CRM/database logging',
        '24/7 availability',
      ],
      cta: 'View Examples',
      popular: false,
    },
    {
      icon: Brain,
      tier: 'Tier 2',
      title: 'AI Agents',
      price: '₹2,00,000 - ₹5,00,000',
      timeline: '4-8 weeks',
      perfectFor: [
        'Review/reputation management',
        'Lead qualification workflows',
        'Data extraction & reporting',
        'Content generation pipelines',
      ],
      features: [
        'Multi-platform integrations',
        'Advanced AI reasoning',
        'Automated workflows',
        'Custom dashboards',
      ],
      cta: 'View Examples',
      popular: false,
    },
    {
      icon: Code,
      tier: 'Tier 3',
      title: 'Custom Software',
      price: '₹5,00,000 - ₹20,00,000',
      timeline: '2-4 months',
      perfectFor: [
        'SaaS products from scratch',
        'Mobile apps with AI backend',
        'Internal business tools',
        'API integrations & platforms',
      ],
      features: [
        'Full product development',
        'AI-powered features',
        'Deployment & hosting',
        'Ongoing maintenance',
      ],
      cta: 'Book Consultation',
      popular: false,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section id="services" className="section-padding bg-[#f8fafc]" aria-label="Services" data-testid="services-section">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#ff6d00] mb-3 block">
            Services
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#212121] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="services-headline"
          >
            What We Build
          </h2>
          <p className="text-base md:text-lg text-[#5a6b8c] max-w-2xl mx-auto">
            From chatbots to enterprise software—all AI-powered
          </p>
        </motion.div>

        {/* Service Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className={`relative bg-white rounded-2xl p-6 md:p-8 border transition-all duration-300 ${
                service.popular
                  ? 'border-[#00e5ff]/50 glow-cyan shadow-lg'
                  : 'border-slate-100 shadow-sm hover:shadow-md'
              }`}
              data-testid={`service-card-${index}`}
            >
              {/* Popular Badge */}
              {service.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-[#00e5ff] to-[#1a237e] text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="mb-6">
                <div className={`w-12 h-12 rounded-xl ${service.popular ? 'bg-gradient-to-br from-[#00e5ff]/20 to-[#1a237e]/20' : 'bg-blue-50'} flex items-center justify-center mb-4`}>
                  <service.icon className={`w-6 h-6 ${service.popular ? 'text-[#1a237e]' : 'text-[#1a237e]'}`} />
                </div>
                <span className="text-xs font-medium text-[#5a6b8c] uppercase tracking-wide">{service.tier}</span>
                <h3
                  className="text-xl md:text-2xl font-bold text-[#212121] mt-1"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {service.title}
                </h3>
                <p className="text-xl md:text-2xl font-bold text-[#1a237e] mt-2">{service.price}</p>
              </div>

              {/* Perfect For */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#212121] mb-3">Perfect for:</h4>
                <ul className="space-y-2">
                  {service.perfectFor.map((item, i) => (
                    <li key={i} className="text-sm text-[#5a6b8c] flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#5a6b8c] mt-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#212121] mb-3">What you get:</h4>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="text-sm text-[#5a6b8c] flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Timeline */}
              <div className="mb-6 py-3 px-4 bg-slate-50 rounded-lg">
                <span className="text-sm text-[#5a6b8c]">Timeline: </span>
                <span className="text-sm font-semibold text-[#1a237e]">{service.timeline}</span>
              </div>

              {/* CTA */}
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block">
                <Button
                  className={`w-full flex items-center justify-center gap-2 ${
                    service.popular ? 'btn-primary' : 'btn-secondary'
                  }`}
                  data-testid={`service-cta-${index}`}
                >
                  {service.cta}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
